import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import directoryService, { UserProfileCardData } from "../../services/directoryService";
import { ALPHABET_LETTERS } from "../../constants/directoryConstants";

// Define the letter data state structure
interface LetterData {
    items: UserProfileCardData[];
    hasNextPage: boolean;
    lastEvaluatedKey: string | null;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

// Define the initial letter data state
const initialLetterData: LetterData = {
    items: [],
    hasNextPage: false,
    lastEvaluatedKey: null,
    status: "idle",
    error: null,
};

// Define the directory state shape
export interface DirectoryState {
    usersByLetter: Record<string, LetterData>; // Map of letter to user data
    activeLetter: string;
    visibleLetters: string[];
    peopleYouMayKnow: {
        items: UserProfileCardData[];
        status: "idle" | "loading" | "succeeded" | "failed";
        error: string | null;
    };
}

// Create the initial state with all alphabet letters
const initialState: DirectoryState = {
    usersByLetter: ALPHABET_LETTERS.reduce(
        (acc, letter) => {
            acc[letter] = { ...initialLetterData };
            return acc;
        },
        {} as Record<string, LetterData>,
    ),
    activeLetter: "A", // Default active letter
    visibleLetters: ["A"], // Default visible letter
    peopleYouMayKnow: {
        items: [],
        status: "idle",
        error: null,
    },
};

// Helper function to shuffle an array
const shuffleArray = (array: string[]) => {
    const newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
};

// Async thunk for fetching random users for the "People You May Know" section
export const fetchRandomUsersForSuggestions = createAsyncThunk(
    "directory/fetchRandomUsersForSuggestions",
    async (_, { rejectWithValue }) => {
        try {
            // 1. Get 3 random letters
            const randomLetters = shuffleArray(ALPHABET_LETTERS).slice(0, 3);

            // 2. Fetch one user for each of the 3 letters in parallel
            const userPromises = randomLetters.map((letter) =>
                directoryService.fetchDirectoryUsers(letter, undefined, undefined),
            );

            const results = await Promise.all(userPromises);

            // 3. Extract the first user from each result, if available
            const randomUsers = results
                .map((result) => result.users?.[0]) // Get the first user from each response
                .filter((user): user is UserProfileCardData => !!user); // Filter out any undefined/null results and ensure type correctness

            return randomUsers;
        } catch (error) {
            console.error(`❌ Redux: Error fetching random users for suggestions`, error);
            return rejectWithValue((error as Error).message || "Failed to load suggestions");
        }
    },
);

// Async thunk for fetching users for a specific letter
export const fetchUsersForLetter = createAsyncThunk(
    "directory/fetchUsersForLetter",
    async (
        {
            letter,
            loadMore = false,
        }: {
            letter: string;
            loadMore?: boolean;
        },
        { getState, rejectWithValue },
    ) => {
        try {
            console.log(`🔍 Redux: Fetching data for letter "${letter}" (loadMore=${loadMore})`);

            const state = getState() as { directory: DirectoryState };
            const letterState = state.directory.usersByLetter[letter] || { ...initialLetterData };

            // If loading more, use the lastEvaluatedKey for pagination
            const exclusiveStartKey = loadMore ? letterState.lastEvaluatedKey : undefined;

            // Skip if we're loading more but there's no next page
            if (loadMore && !letterState.hasNextPage) {
                console.log(
                    `⏭️ Redux: Skipping fetch for letter "${letter}" - no next page available`,
                );
                return { letter, loadMore, data: null };
            }

            // Make the API call
            const response = await directoryService.fetchDirectoryUsers(
                letter,
                undefined,
                exclusiveStartKey as string | undefined,
            );

            console.log(`✅ Redux: Received ${response.users.length} users for letter "${letter}"`);

            return {
                letter,
                loadMore,
                data: response,
            };
        } catch (error) {
            console.error(`❌ Redux: Error fetching users for letter "${letter}"`, error);
            return rejectWithValue((error as Error).message || "Failed to load users");
        }
    },
);

// Create the directory slice
const directorySlice = createSlice({
    name: "directory",
    initialState,
    reducers: {
        setActiveLetter: (state, action: PayloadAction<string>) => {
            state.activeLetter = action.payload;
        },
        setVisibleLetters: (state, action: PayloadAction<string[]>) => {
            state.visibleLetters = action.payload;
        },
        clearDirectoryData: (state) => {
            // Reset to initial state
            state.usersByLetter = ALPHABET_LETTERS.reduce(
                (acc, letter) => {
                    acc[letter] = { ...initialLetterData };
                    return acc;
                },
                {} as Record<string, LetterData>,
            );
            state.activeLetter = "A";
            state.visibleLetters = ["A"];
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchUsersForLetter pending
            .addCase(fetchUsersForLetter.pending, (state, action) => {
                const { letter } = action.meta.arg;
                console.log(`⏳ Redux: Setting status to loading for letter "${letter}"`);

                if (!state.usersByLetter[letter]) {
                    state.usersByLetter[letter] = { ...initialLetterData };
                }
                state.usersByLetter[letter].status = "loading";
                state.usersByLetter[letter].error = null;
            })
            // Handle fetchUsersForLetter fulfilled
            .addCase(fetchUsersForLetter.fulfilled, (state, action) => {
                const { letter, loadMore, data } = action.payload;

                // If no data returned (could happen if loadMore with no next page)
                if (!data) {
                    console.log(`⚠️ Redux: No data returned for letter "${letter}"`);
                    return;
                }

                console.log(
                    `📥 Redux: Processing data for letter "${letter}": ${data.users.length} users`,
                );

                // Ensure the letter data exists
                if (!state.usersByLetter[letter]) {
                    state.usersByLetter[letter] = { ...initialLetterData };
                }

                const letterData = state.usersByLetter[letter];

                // Update the letter data
                const newItems = loadMore
                    ? [...(letterData.items || []), ...(data.users || [])] // Append for pagination with safe defaults
                    : data.users || []; // Replace for new letter with safe default

                // Deduplicate based on user ID to prevent React key warnings and ensure data integrity
                const uniqueItems = Array.from(
                    new Map(newItems.map((item) => [item.id, item])).values(),
                );

                state.usersByLetter[letter] = {
                    items: uniqueItems, // Use uniqueItems
                    hasNextPage: data.hasNextPage || false,
                    lastEvaluatedKey: data.lastEvaluatedKey || null,
                    status: "succeeded",
                    error: null,
                };

                console.log(
                    `✅ Redux: Updated state for letter "${letter}". Items count: ${state.usersByLetter[letter].items.length}`,
                );
            })
            // Handle fetchUsersForLetter rejected
            .addCase(fetchUsersForLetter.rejected, (state, action) => {
                const { letter } = action.meta.arg;
                console.log(`❌ Redux: Request failed for letter "${letter}"`);

                // Ensure the letter data exists
                if (!state.usersByLetter[letter]) {
                    state.usersByLetter[letter] = { ...initialLetterData };
                }
                state.usersByLetter[letter].status = "failed";
                state.usersByLetter[letter].error = action.payload as string;
            })
            // Handle fetchRandomUsersForSuggestions
            .addCase(fetchRandomUsersForSuggestions.pending, (state) => {
                state.peopleYouMayKnow.status = "loading";
                state.peopleYouMayKnow.error = null;
            })
            .addCase(fetchRandomUsersForSuggestions.fulfilled, (state, action) => {
                state.peopleYouMayKnow.status = "succeeded";
                state.peopleYouMayKnow.items = action.payload;
            })
            .addCase(fetchRandomUsersForSuggestions.rejected, (state, action) => {
                state.peopleYouMayKnow.status = "failed";
                state.peopleYouMayKnow.error = action.payload as string;
            });
    },
});

// Export actions
export const { setActiveLetter, setVisibleLetters, clearDirectoryData } = directorySlice.actions;

// Export the reducer
export default directorySlice.reducer;
